import { ComponentType } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { InfiniteQueryResult } from '@frontend/types';
import { theme } from '@frontend/theme';
import { SkeletonLoader } from '@frontend/design-system';
import { ScheduleRequestContextProvider } from '../Context/ScheduleRequestContext';
import { useScheduleRequestModalContextValue } from '../hooks';
import { useScheduleRequestContextValue } from '../hooks/use-schedule-request-context-value';
import { ScheduleRequestContentWrapper } from '../ScheduleRequestModal/types';
import { ScheduleRequestFormManager } from './ScheduleRequestFormManager';
import { ScheduleRequestItemHeaderProps } from './ScheduleRequestItemHeader';

const FOOTER_HEIGHT = 53;

type ScheduleRequestListContentProps = {
  height?: number | string;
  refetchScheduleRequestList: () => void;
  infiniteListQuery: InfiniteQueryResult<Record<'rows', ScheduleRequest[]>>;
  modalWrapper: ScheduleRequestContentWrapper;
  scheduleRequestListItem: ComponentType<ScheduleRequestItemHeaderProps>;
};

export const ScheduleRequestListContent = ({
  height,
  refetchScheduleRequestList,
  infiniteListQuery,
  modalWrapper: ModalWrapper,
  scheduleRequestListItem: ScheduleRequestListItem,
}: ScheduleRequestListContentProps) => {
  const { t } = useTranslation('scheduleCalendarActions');

  const { selectedScheduleRequest, setSelectedScheduleRequest, scheduleRequestModalContextValue } =
    useScheduleRequestModalContextValue({ refetchScheduleRequestList });

  const { providerValue } = useScheduleRequestContextValue({ refetchScheduleRequestList });

  return (
    <>
      <ScheduleRequestContextProvider value={providerValue}>
        <InfinitePaginatedList
          infiniteQueryProps={infiniteListQuery}
          height={typeof height === 'number' ? height - FOOTER_HEIGHT : height}
          skeletonLoader={
            <SkeletonLoader height={50} width={`calc(100% - ${theme.spacing(4)})`} css={{ margin: theme.spacing(2) }} />
          }
          emptyStateConfig={{
            type: 'schedule_requests',
            description: t('No New Requests'),
            header: '',
          }}
          renderListItem={({ listItem }) => (
            <ScheduleRequestListItem
              listItem={listItem}
              setSelectedScheduleRequest={setSelectedScheduleRequest}
              scheduleRequestModalProps={scheduleRequestModalContextValue.scheduleRequestModalProps}
            />
          )}
        />
      </ScheduleRequestContextProvider>
      {selectedScheduleRequest && (
        <ScheduleRequestFormManager
          wrapper={ModalWrapper}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequest}
        />
      )}
    </>
  );
};
